// SMS message-preview widget (MIS-971). Mounts beneath the message-copy fields
// on the Auto Send tab and the campaign copy editor. A "Preview" button renders
// a centered, phone-style SMS bubble showing a REAL composed message from the
// CURRENT form copy (the same composition the dispatcher uses — see
// lib/smsPreview.ts, a verbatim port of rtb-api buildSmsBody). The button then
// relabels to "Spin"; each Spin advances a seed counter so the operator cycles
// through genuine opener/action/cta combinations. A close (X) hides the bubble
// and resets the label back to "Preview".
//
// Copy is pulled LIVE from props (the parent's current form values), so what
// the operator sees is exactly what would send for that copy + sample name.

import { useState } from "react";

import { composeSmsPreview, SAMPLE_FIRST_NAME } from "../lib/smsPreview";
import { buttonStyle, tokens } from "../lib/theme";

// Seed the cycle somewhere non-trivial so the first preview isn't always
// index 0 across the three groups; each Spin does seed++.
const INITIAL_SEED = 1;

export interface SmsPreviewProps {
  openers: string[];
  openers_no_name: string[];
  actions: string[];
  ctas: string[];
  callback_did: string;
  footer: string;
  /** Optional override for the sample first name ({n}); defaults to "John". */
  firstName?: string;
}

export default function SmsPreview({
  openers,
  openers_no_name,
  actions,
  ctas,
  callback_did,
  footer,
  firstName = SAMPLE_FIRST_NAME,
}: SmsPreviewProps) {
  const [open, setOpen] = useState(false);
  const [seed, setSeed] = useState(INITIAL_SEED);

  // A message needs an opener + an action; the CTA is OPTIONAL (the operator
  // may put the call-to-action in the action field and leave CTAs blank —
  // buildSmsBody renders nothing for an empty/blank CTA). Only guard on the two
  // required groups so we never render an empty bubble, but don't force a CTA.
  const canPreview = openers.length > 0 && actions.length > 0;

  const body = canPreview
    ? composeSmsPreview({
        openers,
        openers_no_name,
        actions,
        ctas,
        callback_did,
        footer,
        firstName,
        seed,
      })
    : "";

  const onClick = (): void => {
    if (!canPreview) return;
    if (!open) {
      setOpen(true);
    } else {
      // Spin: advance to a different real combination.
      setSeed((s) => s + 1);
    }
  };

  const onClose = (): void => {
    setOpen(false);
    setSeed(INITIAL_SEED);
  };

  return (
    <div style={{ marginTop: tokens.space.lg }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: tokens.space.md,
          flexWrap: "wrap",
        }}
      >
        <button
          type="button"
          onClick={onClick}
          disabled={!canPreview}
          style={buttonStyle("subtle", !canPreview)}
          aria-label={open ? "Spin to a new message preview" : "Preview message"}
        >
          {open ? "Spin" : "Preview"}
        </button>
        {!canPreview && (
          <span
            style={{ color: tokens.color.fg2, fontSize: tokens.font.size.sm }}
          >
            Add at least one opener and action to preview a message.
          </span>
        )}
        {open && (
          <span
            style={{ color: tokens.color.fg2, fontSize: tokens.font.size.sm }}
          >
            Sample name {firstName} · {body.length} chars
          </span>
        )}
      </div>

      {open && canPreview && (
        <PhoneBubble body={body} combo={seed} onClose={onClose} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The centered iMessage/SMS-style chat bubble (rounded + tail), with a char
// count, a "combo N" indicator, and a close (X) affordance.
// ---------------------------------------------------------------------------

function PhoneBubble({
  body,
  combo,
  onClose,
}: {
  body: string;
  combo: number;
  onClose: () => void;
}) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        marginTop: tokens.space.lg,
      }}
    >
      <div
        style={{
          position: "relative",
          width: "100%",
          maxWidth: 360,
          background: tokens.color.bg1,
          border: `1px solid ${tokens.color.border}`,
          borderRadius: tokens.radius.lg,
          boxShadow: tokens.shadow.elevated,
          padding: tokens.space.lg,
        }}
      >
        {/* Close (X) */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close preview"
          style={{
            position: "absolute",
            top: tokens.space.sm,
            right: tokens.space.sm,
            width: 24,
            height: 24,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: tokens.radius.pill,
            border: `1px solid ${tokens.color.borderMid}`,
            background: "transparent",
            color: tokens.color.fg1,
            fontSize: tokens.font.size.sm,
            lineHeight: 1,
            cursor: "pointer",
          }}
        >
          ×
        </button>

        <div
          style={{
            fontSize: tokens.font.size.xs,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: tokens.color.fg1,
            fontWeight: 600,
            marginBottom: tokens.space.md,
          }}
        >
          Message preview
        </div>

        {/* The chat bubble — left-aligned incoming SMS look with a tail. */}
        <div style={{ display: "flex", justifyContent: "center", paddingBottom: tokens.space.sm }}>
          <div
            style={{
              position: "relative",
              maxWidth: 300,
              background: tokens.gradient.brand,
              color: tokens.color.bg0,
              padding: "10px 14px",
              borderRadius: 18,
              fontSize: tokens.font.size.base,
              lineHeight: 1.4,
              fontFamily: tokens.font.family,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              boxShadow: tokens.shadow.pillBrand,
            }}
          >
            {body}
            {/* Bubble tail (bottom-left). */}
            <span
              aria-hidden
              style={{
                position: "absolute",
                bottom: -2,
                left: -5,
                width: 14,
                height: 14,
                background: tokens.color.teal,
                borderBottomRightRadius: 14,
                clipPath: "polygon(100% 0, 100% 100%, 0 100%)",
              }}
            />
          </div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: tokens.space.md,
            marginTop: tokens.space.md,
            color: tokens.color.fg2,
            fontSize: tokens.font.size.xs,
            fontFamily: tokens.font.mono,
          }}
        >
          <span>combo {combo}</span>
          <span>{body.length} chars</span>
        </div>
      </div>
    </div>
  );
}
